import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { Box, Typography } from "@mui/material";
import { useOperationDespatchContext } from "../../../../contexts/DespatchContext";
import {
  CreateTaskPayload,
  useDespatchActions,
} from "../../../Diary/views/DiaryResourcesView/hooks/useDespatchActions";
import { Color } from "Components/Form/Button";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useHandleValidate } from "Hooks/useYupValidation";
import { Job } from "Models/job";
import { Searches } from "Models/shipeezi.table";
import { Task, TaskStartDatePeriod } from "Models/task";
import { Team } from "Models/team";
import { useTranslation } from "react-i18next";
import { getMuiPopupContainer } from "Themes/dispatchTheme";
import { TaskStatus, TaskType } from "Util/enums";
import { getOrderCustomerDisplayName } from "Util/names";
import * as yup from "yup";

import { useCreateTaskForm } from "../../../../hooks/useCreateTaskForm";
import { MainComponent } from "./menucard";
import { useStyles, StyledModal } from "./styles";
import { zonedTimeToUtc } from "date-fns-tz";
import { formatSimpleDate } from "Util/formatDateAndTime";
import { createTaskValidationSchema } from "./createTaskValidationSchema";
import { useAppState } from "Context/StateContext";

const NODATA = require("Assets/img/EmptyInbox@2x.png");

type TeamListOption = Pick<Team, "id" | "name">;

type Props = {
  onSuccessTaskCreated?: (response: Task, variables: CreateTaskPayload) => void;
};
export type DespatchCreateTaskModalHandles = {
  open: (teamList: TeamListOption[]) => void;
  close: () => void;
};

const DespatchCreateTaskComponent: ForwardRefRenderFunction<
  DespatchCreateTaskModalHandles,
  Props
> = (props, ref) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const { currentDate, companyId } = useOperationDespatchContext();
  const { formatWithUserTimezone, userTimezone } = useAppState();
  const { getOrders, getAllTaskTypes, useCreateTask } = useDespatchActions();

  const { data: taskTypes = [], isLoading: isLoadingTypes } = getAllTaskTypes();
  const { mutate: createTask, isLoading: isLoadingTasks } = useCreateTask((response, variables) =>
    props.onSuccessTaskCreated?.(response, variables)
  );

  const filters = useMemo(
    () => [
      {
        searchFor: "reference",
        label: translate("reference"),
      },
      {
        searchFor: "secondReference",
        label: translate("secondReference"),
      },
      {
        searchFor: "customer",
        label: translate("customer"),
      },
      {
        searchFor: "origins",
        label: translate("origin"),
      },
      {
        searchFor: "destinations",
        label: translate("destination"),
      },
    ],
    []
  );
  const [searchFilter, setSearchFilter] = useState<string>(filters[0].searchFor);
  const [search, setSearch] = useState<Searches>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [orders, setOrders] = useState<Job[]>([]);
  const [order, setOrder] = useState<Job | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const [teamList, setTeamList] = useState<TeamListOption[]>([]);

  // eslint-disable-next-line unused-imports/no-unused-vars, @typescript-eslint/no-unused-vars
  const [teamIdSelected, setTeamIdSelected] = useState<number | null>(null);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { data: ordersData, isFetching: isLoadingOrders } = getOrders(companyId, {
    searches: search,
    page: 0,
    pageSize: 5,
  });

  const isLoading = useMemo(
    () => isLoadingTasks || isLoadingOrders,
    [isLoadingTasks, isLoadingOrders]
  );

  const filteredTaskTypes = useMemo(() => {
    const taskTypesToHide = [TaskType.HOME_SURVEY, TaskType.VIDEO_SURVEY];

    return taskTypes?.filter((taskType) => !taskTypesToHide.includes(taskType?.name as TaskType));
  }, [taskTypes]);

  useEffect(() => {
    if (ordersData) {
      setOrders(ordersData);
    }
  }, [ordersData]);

  useEffect(() => {
    if (searchValue.length) {
      setSearch([
        {
          [searchFilter as string]: searchValue,
        },
      ]);

      return;
    }
    setSearch([]);
  }, [searchValue]);

  const { form, task, isMultiDayTask, handleChangePeriod, clearAllFields } = useCreateTaskForm({
    errors,
    isLoading,
    taskTypes: filteredTaskTypes,
    isLoadingTypes,
    currentDate,
  });

  const schema = useMemo(
    () => createTaskValidationSchema({ translate, formatWithUserTimezone }),
    [translate, formatWithUserTimezone]
  );

  const handleValidate = () => {
    const validated = useHandleValidate(
      {
        isMultiDayTask,
        ...task,
      },
      schema,
      setErrors
    );

    return validated;
  };

  const getStartAndEndDate = () => {
    if (!task.timeSlots?.length) {
      return { startDate: task.startDate, endDate: task.endDate };
    }

    const currentTimeSlots = task.timeSlots.sort(
      (a, b) =>
        zonedTimeToUtc(a.date!, userTimezone.name).getTime() -
        zonedTimeToUtc(b.date!, userTimezone.name).getTime()
    );

    const startCurrentDate = formatSimpleDate(new Date(currentTimeSlots?.[0]?.date as string), {
      formatString: "yyyy-MM-dd",
      isUTC: true,
    });

    const indexOfLastDate = currentTimeSlots?.length - 1;
    const endCurrentDate = formatSimpleDate(
      new Date(currentTimeSlots?.[indexOfLastDate]?.date as string),
      { formatString: "yyyy-MM-dd", isUTC: true }
    );

    const startCurrentTime = currentTimeSlots?.[0]?.from;
    const currentStartDate = new Date(`${startCurrentDate}T${startCurrentTime}`);

    const startDate = zonedTimeToUtc(currentStartDate, userTimezone.name).toISOString();

    const endCurrentTime = currentTimeSlots?.[indexOfLastDate]?.from;
    const currentEndDate = new Date(`${endCurrentDate}T${endCurrentTime}`);

    const endDate = zonedTimeToUtc(currentEndDate, userTimezone.name).toISOString();

    return {
      startDate,
      endDate,
    };
  };

  const onSubmit = () => {
    setIsSubmitted(true);
    if (!task.startDate) {
      NotificationToast.warning(translate("pleaseSelectAStartDate"), {
        id: "pleaseSelectAStartDate",
      });

      return;
    }

    if (!handleValidate()) return;
    if (!order?.id) return;

    const { startDate, endDate } = getStartAndEndDate();

    const createTaskPayload = {
      data: {
        jobId: order?.id,
        type: task.type,
        startDate,
        endDate,
        period: task.period,
        timeSlots: task.timeSlots,
        status: TaskStatus.REGISTERED,
      } as Task,
      teamId: teamIdSelected,
    };

    createTask(createTaskPayload, {
      onSuccess: () => {
        onCloseModal();
      },
    });
  };

  const [isOpen, setIsOpen] = useState(false);

  const onOpenModal = useCallback(
    (_teams: TeamListOption[]) => {
      setTeamList(_teams || []);
      handleChangePeriod({ period: TaskStartDatePeriod.MORNING, currentDate });
      setIsOpen(true);
    },
    [currentDate]
  );

  const onCloseModal = useCallback(() => {
    setIsSubmitted(false);
    setIsOpen(false);
    setSearchFilter(filters[0].searchFor);
    setSearch([]);
    setSearchValue("");
    setOrder(null);
    setOrders([]);
    clearAllFields();
    setErrors([]);
  }, []);

  useImperativeHandle(ref, () => ({
    open: onOpenModal,
    close: onCloseModal,
  }));

  return (
    <StyledModal
      container={getMuiPopupContainer}
      title={translate("newTask")}
      open={isOpen}
      handleClose={onCloseModal}
      className={classes.modal}
      data-cy="modalnewTask"
      dataCyCloseButton="modalNewTaskCloseButton"
      showActionButtons
      modalFooterCustomClass={classes.modalFooter}
      actionButtonProps={{
        textSave: translate("create"),
        customColorCancel: Color.TEXT_INFO,
        saveDisabled: isLoading,
        cancelDisabled: false,
        loading: isLoading,
        onCancel: () => onCloseModal(),
        onSave: onSubmit,
      }}
    >
      <Grid container direction="row" spacing={2} alignItems={"flex-end"}>
        <Grid item xs={12} md={9}>
          <MainComponent
            isLoading={isLoadingOrders}
            options={orders}
            onItemSelect={setOrder}
            searchQuery={searchValue}
            setSearchQuery={setSearchValue}
            filterQuery={searchFilter}
            setFilterQuery={setSearchFilter}
            filters={filters}
          />
        </Grid>

        <Grid item xs={12}>
          <Box className={classes.GreyBox}>
            {!order && (
              <Box className={classes.noDataContainer}>
                <img style={{ height: "150px" }} src={NODATA}></img>
                <Typography className={classes.nodatatext}>
                  {translate("itLooksLikeNoOrderWasSelectedYet")}
                </Typography>
                <Typography className={classes.nodatasubtext}>
                  {translate("startSearchingForAnOrderYoBeAbleToCreateANewTask")}
                </Typography>
              </Box>
            )}
            {order?.id && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography className={classes.slectedCard}>
                      {translate("newTaskFor")} #{order?.reference} - {getOrderCustomerDisplayName(order)}
                    </Typography>
                  </Grid>
                  {form}
                </Grid>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </StyledModal>
  );
};

const DespatchCreateTask = forwardRef(DespatchCreateTaskComponent);

export { DespatchCreateTask };
